import { useCallback, useEffect, useState } from "react";

import { THEME_STORAGE_KEY } from "../lib/config";

export type ColorScheme = "light" | "dark";

function getInitialScheme(): ColorScheme {
  if (typeof window === "undefined") {
    return "light";
  }
  const stored = window.localStorage.getItem(
    THEME_STORAGE_KEY
  ) as ColorScheme | null;
  if (stored === "light" || stored === "dark") {
    return stored;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function useColorScheme() {
  const [scheme, setScheme] = useState<ColorScheme>(getInitialScheme);
  const [mounted, setMounted] = useState(false);

  // Sync scheme on mount to handle SSR/client mismatch
  useEffect(() => {
    setMounted(true);
    const actualScheme = getInitialScheme();
    setScheme((current) => {
      // Only update if different to avoid unnecessary re-renders
      return actualScheme !== current ? actualScheme : current;
    });
  }, []);

  useEffect(() => {
    if (!mounted || typeof document === "undefined") return;
    const root = document.documentElement;
    if (scheme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    window.localStorage.setItem(THEME_STORAGE_KEY, scheme);
  }, [scheme, mounted]);

  const toggle = useCallback(() => {
    setScheme((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  const setExplicit = useCallback((value: ColorScheme) => {
    setScheme(value);
  }, []);

  return { scheme, toggle, setScheme: setExplicit, mounted };
}
